import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase.js";
import { Link, useLocation } from "react-router-dom";
import { normalizeStoreImagePath } from "../utils/storeImagePaths.js";
import {
  getProductOrderId,
  getRecentlyViewedProductMap,
} from "../utils/listingSessionStorage.js";

const FallbackImg =
  "https://via.placeholder.com/300x300?text=DecorZoom+Product";

const DEFAULT_CATEGORIES = Object.freeze([
  "Balloons",
  "Foil Balloons",
  "Banners",
  "Caps",
  "Party Props",
]);

export default function DecorZoomStore() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");
  const [viewedProducts, setViewedProducts] = useState(() => getRecentlyViewedProductMap());
  const location = useLocation();

  // fetch
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, "STORE_PRODUCTS"));
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        data.sort(
          (a, b) =>
            (b.updatedAt || 0) - (a.updatedAt || 0) ||
            (a.title || "").localeCompare(b.title || "")
        );
        setProducts(data);
      } catch (e) {
        console.error("❌ STORE_PRODUCTS fetch error:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const refreshViewedProducts = () => {
      setViewedProducts(getRecentlyViewedProductMap());
    };

    refreshViewedProducts();
    window.addEventListener("focus", refreshViewedProducts);

    return () => {
      window.removeEventListener("focus", refreshViewedProducts);
    };
  }, []);

  // sidebar category list
  const categories = useMemo(() => {
    const fromDB = Array.from(
      new Set(products.map((p) => p.category).filter(Boolean))
    );
    return Array.from(new Set(["All", ...DEFAULT_CATEGORIES, ...fromDB]));
  }, [products]);

  // filters
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return products.filter((p) => {
      const inCategory = category === "All" || p.category === category;
      const matchQ =
        !q ||
        (p.title || "").toLowerCase().includes(q) ||
        (p.category || "").toLowerCase().includes(q) ||
        (p.description || "").toLowerCase().includes(q);
      return inCategory && matchQ;
    });
  }, [products, search, category]);

  const itemListSchema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "DecorZoom Store - Balloon & Party Decoration Products",
      url: "https://www.decorzoom.in/store",
      mainEntity: {
        "@type": "ItemList",
        itemListElement: products.map((p, index) => ({
          "@type": "ListItem",
          position: index + 1,
          url: `https://www.decorzoom.in/store/product/${encodeURIComponent(p.id)}`,
          name: p.title || p.id,
        })),
      },
    }),
    [products]
  );

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <title>DecorZoom Store - Balloon & Party Decoration Products Online</title>
      <meta
        name="description"
        content="Shop balloons, foil balloons, banners, caps and party decoration props online from DecorZoom. Fast delivery across Delhi NCR for birthdays and celebrations."
      />
      <link rel="canonical" href="https://www.decorzoom.in/store" />
      <meta property="og:title" content="DecorZoom Store - Balloon & Party Decoration Products" />
      <meta
        property="og:description"
        content="Shop balloons, banners, caps and party decoration props online from DecorZoom for birthdays and celebrations across Delhi NCR."
      />
      <meta property="og:url" content="https://www.decorzoom.in/store" />
      <meta property="og:type" content="website" />
      {products.length ? <script type="application/ld+json">{JSON.stringify(itemListSchema)}</script> : null}

      <h1 className="mb-4 text-2xl font-bold text-gray-900 sm:text-3xl">
        DecorZoom Store — Balloon &amp; Party Decoration Products
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
      {/* -------- Sidebar (desktop) -------- */}
      <aside className="hidden md:block md:col-span-3">
        <div className="bg-white rounded-2xl border shadow-sm p-3 md:sticky md:top-20">
          <h2 className="text-lg font-semibold mb-2">Categories</h2>
          <nav className="space-y-1">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                className={`w-full text-left px-4 py-2 rounded-lg ${
                  category === c
                    ? "bg-green-50 text-green-700 border border-green-300"
                    : "hover:bg-gray-50"
                }`}
                aria-pressed={category === c}
              >
                {c}
              </button>
            ))}
          </nav>
        </div>
      </aside>

      {/* -------- Main -------- */}
      <main className="md:col-span-9">
        {/* Mobile category chips */}
        <div className="md:hidden mb-3 -mt-1">
          <div className="flex gap-2 overflow-x-auto no-scrollbar py-1">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                className={`px-3 py-1.5 rounded-full border text-sm whitespace-nowrap ${
                  category === c
                    ? "bg-green-600 text-white border-green-600"
                    : "bg-white text-gray-700"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        {/* Search */}
        <div className="flex items-center justify-center mb-3">
          <input
            type="search"
            placeholder="Search products..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-2xl border px-4 py-3 text-base outline-none focus:ring-2 focus:ring-green-500 text-center"
          />
        </div>

        {/* Info line */}
        <div className="flex items-center justify-between text-xs sm:text-sm text-gray-600 mb-3">
          <div className="truncate">
            Showing <b>{filtered.length}</b> item(s)
            {category !== "All" && <> in <b>{category}</b></>}
            {search && <> for “<b>{search}</b>”</>}
          </div>
          {(category !== "All" || search) && (
            <button
              onClick={() => {
                setCategory("All");
                setSearch("");
              }}
              className="underline hover:text-gray-800 shrink-0"
            >
              Clear filters
            </button>
          )}
        </div>

        {/* Grid */}
        {loading ? (
          <div className="text-center py-12 text-green-600 font-semibold">
            Loading...
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No products found. Try another category or search.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {filtered.map((p) => {
              const v0 = p.variants?.[0] || {};
              const img =
                normalizeStoreImagePath(p.mainImage) ||
                normalizeStoreImagePath(v0.images?.[0]) ||
                FallbackImg;
              const alt = v0.altTexts?.[0] || p.title || p.id;

              const price =
                (typeof p.unitPrice === "number" ? p.unitPrice : null) ??
                (typeof v0.unitPrice === "number" ? v0.unitPrice : null) ??
                0;

              const mrp =
                (typeof p.unitMrp === "number" ? p.unitMrp : null) ??
                (typeof v0.unitMrp === "number" ? v0.unitMrp : null) ??
                0;

              const discount =
                mrp && price && price < mrp
                  ? Math.round(((mrp - price) / mrp) * 100)
                  : null;
              const viewedAt = viewedProducts.get(getProductOrderId(p));
              const isViewed = Boolean(viewedAt);
              const viewedLabel =
                viewedAt && Date.now() - viewedAt < 30 * 60 * 1000
                  ? "Just Watched"
                  : "Viewed";

              return (
                <Link
                  key={p.id}
                  to={`/store/product/${encodeURIComponent(p.id)}`}
                  state={{ from: `${location.pathname}${location.search}${location.hash}` }}
                  className={`group relative block bg-white rounded-2xl border shadow-sm overflow-hidden hover:shadow-md transition ${
                    isViewed ? "border-emerald-200 bg-emerald-50/40" : ""
                  }`}
                  title="Click for details"
                >
                  {isViewed && (
                    <div className="absolute right-2 top-2 z-10">
                      <span className="rounded-full bg-white/95 px-2 py-0.5 text-[10px] font-bold text-emerald-700 shadow-sm ring-1 ring-emerald-200 sm:px-2.5 sm:py-1">
                        {viewedLabel}
                      </span>
                    </div>
                  )}

                  <div className="aspect-[4/5] sm:aspect-square bg-gray-100 overflow-hidden">
                    <img
                      src={img}
                      alt={alt}
                      className="w-full h-full object-contain group-hover:scale-105 transition-transform"
                      loading="lazy"
                      onError={(e) => (e.currentTarget.src = FallbackImg)}
                    />
                  </div>
                  <div className="p-2 sm:p-3">
                    <h3 className="text-sm sm:text-[15px] font-semibold leading-snug line-clamp-2 min-h-[2.5rem]">
                      {p.title}
                    </h3>
                    <div className="mt-1 flex items-center gap-2">
                      <span className="text-base sm:text-lg font-bold text-gray-900">
                        ₹{price}
                      </span>
                      {discount && (
                        <>
                          <span className="text-xs sm:text-sm line-through text-gray-400">
                            ₹{mrp}
                          </span>
                          <span className="text-[10px] sm:text-xs font-semibold text-green-600">
                            {discount}% OFF
                          </span>
                        </>
                      )}
                    </div>
                    <p className="mt-0.5 text-[11px] sm:text-xs text-gray-500">
                      {p.category}
                    </p>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </main>
      </div>
    </div>
  );
}
